import React, { useState, useMemo } from 'react';
import { Table, Form, InputGroup, Button, Row, Col, Pagination } from 'react-bootstrap';
import * as XLSX from 'xlsx';

const ReportsTable = ({
  data,
  reportType,
  columns,
  title,
  cellPaddingY = 10,
  cellPaddingX = 16
}) => {
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(8);

  // Initialize column filters dynamically based on columns
  useMemo(() => {
    const filters = {};
    columns.forEach(col => {
      if (col.key !== 'actions') {
        filters[col.key] = '';
      }
    });
    setColumnFilters(filters);
  }, [columns]);

  // Filter data based on search and column filters
  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Global search
    if (searchQuery) {
      filtered = filtered.filter(row =>
        Object.values(row).some(value =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    // Column-wise filters
    Object.keys(columnFilters).forEach(key => {
      if (columnFilters[key]) {
        filtered = filtered.filter(row =>
          String(row[key]).toLowerCase().includes(columnFilters[key].toLowerCase())
        );
      }
    });

    // Date range filter (if date column exists)
    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter(row => {
        const dateField = row.date || row.transactionDate || row.createdAt;
        if (dateField) {
          const rowDate = new Date(dateField);
          const startDate = new Date(dateRange.start);
          const endDate = new Date(dateRange.end);
          return rowDate >= startDate && rowDate <= endDate;
        }
        return true;
      });
    }

    return filtered;
  }, [data, searchQuery, columnFilters, dateRange]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Handle column filter change
  const handleColumnFilterChange = (column, value) => {
    setColumnFilters(prev => ({ ...prev, [column]: value }));
    setCurrentPage(1);
  };

  // Handle date range change
  const handleDateRangeChange = (type, value) => {
    setDateRange(prev => ({ ...prev, [type]: value }));
    setCurrentPage(1);
  };

  // Export to Excel
  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, reportType);
    XLSX.writeFile(workbook, `${reportType}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };


  // Pagination component
  const renderPagination = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // Previous button
    items.push(
      <Pagination.Prev 
        key="prev" 
        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
      />
    );

    // Page numbers
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    // Next button
    items.push(
      <Pagination.Next 
        key="next" 
        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
      />
    );

    return items;
  };

  // Render cell value with appropriate formatting
  const renderCellValue = (row, column) => {
    const value = row[column.key];

    if (typeof column.render === 'function') {
      return column.render(row, value, column);
    }

    if (column.key === 'amount' || column.key === 'credit' || column.key === 'debit' || column.key === 'balance') {
      return `₹${parseFloat(value || 0).toFixed(2)}`;
    }

    return value;
  };

  return (
    <div className="user-table-container">
      {/* Header with Title, Search and Export */}
      <Row className="mb-3 g-2 align-items-center">
        <Col lg={4} md={6} sm={12}>
          <h4 className="mb-0" style={{ color: '#1e293b', fontWeight: '700', fontSize: '1.5rem' }}>
            {title || reportType}
          </h4>
        </Col>
        <Col lg={8} md={6} sm={12} className="d-flex justify-content-end gap-2">
          <InputGroup style={{ maxWidth: '300px' }}>
            <InputGroup.Text>
              <i className="bi bi-search"></i>
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </InputGroup>
          <Button 
            size="sm" 
            variant="success" 
            onClick={handleExportExcel}
            style={{
              padding: '0.4rem 1rem 0.5rem 1rem',
              fontSize: '0.875rem',
              height: 'auto'
            }}
          >
            <i className="bi bi-file-earmark-excel me-1"></i> Excel
          </Button>
        </Col>
      </Row>

      {/* Table with Horizontal Scroll */}
      <div className="table-responsive">
        <Table bordered hover className="modern-table mb-0">
          <thead 
            style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              color: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}
          >
            <tr>
              {columns.map((column) => (
                <th 
                  key={column.key}
                  style={{
                    padding: `${cellPaddingY + 2}px ${cellPaddingX}px`,
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    textAlign: 'center',
                    borderBottom: '2px solid rgba(255,255,255,0.2)'
                  }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData.length > 0 ? (
              currentData.map((row, index) => (
                <tr 
                  key={index} 
                  style={{ 
                    background: '#f0f2f5',
                    transition: 'background 0.2s ease'
                  }}
                >
                  {columns.map((column) => (
                    <td 
                      key={column.key}
                      style={{
                        padding: `${cellPaddingY}px ${cellPaddingX}px`,
                        fontSize: '0.875rem',
                        verticalAlign: 'middle',
                        textAlign: 'center'
                      }}
                    >
                      {renderCellValue(row, column)}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                  <span className="text-muted">No data found</span>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      
      {/* Scroll Indicator - Only shows on mobile/tablet */}
      <div 
        className="d-lg-none text-center mt-2"
        style={{
          fontSize: '0.75rem',
          color: '#64748b',
          opacity: 0.7
        }}
      >
        <i className="bi bi-arrows-move me-1"></i>
        Scroll horizontally to view all columns
      </div>

      {/* Pagination Controls */}
      <Row className="align-items-center mt-4 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
        <Col lg={6} md={6} sm={12} className="mb-2 mb-md-0">
          <span style={{ 
            fontSize: '0.9rem', 
            color: '#64748b',
            fontWeight: '500'
          }}>
            Showing <strong style={{ color: '#667eea' }}>{startIndex + 1}</strong> to <strong style={{ color: '#667eea' }}>{Math.min(endIndex, filteredData.length)}</strong> of <strong style={{ color: '#667eea' }}>{filteredData.length}</strong> entries
          </span>
        </Col>
        <Col lg={6} md={6} sm={12} className="d-flex justify-content-end">
          <Pagination className="mb-0">{renderPagination()}</Pagination>
        </Col>
      </Row>
    </div>
  );
};

export default ReportsTable;

